'use client'

import { memo, useEffect, useMemo } from 'react'

import { StoreBonusStatusResponse } from '@common/models/bonus'
import cx from 'classnames'
import { m } from 'framer-motion'
import Image, { getImageProps } from 'next/image'
import { useTranslations } from 'next-intl'

import BonusTrackPointsIcon from '@/assets/images/storefronts/bonus-track-points-icon.svg'
import BrawlFlare from '@/assets/images/storefronts/brawlstars/rays.png'
import { useCartContext } from '@/contexts/CartContext'
import { useModalContext } from '@/contexts/ModalContext'
import { setBonusTrackCompleted } from '@/features/common/commonSlice'
import { getBackgroundImage } from '@/features/storefronts/_storeBase/assetRendering/imageUtils'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { hasUnclaimedBonusesCookie } from '@/utils/cookies'
import { useAppDispatch } from '@/utils/useAppDispatch'

import { ItemDeliveryIndicator } from './ItemDeliveryIndicator'
import {
  extractBonusValues,
  getDeliveredBonuses,
  getNextItem,
  getProgressPercentage,
  getRemainingPoints,
  getBonusTrackCompletionStatus,
} from '../../bonus/bonusUtils'
import { DrawerButton } from '../../bonusStatusFooter/DrawerButton'
import { StoreTypography } from '../../typography/StoreTypography'
import { BonusItemImage } from '../shared/BonusItemImage'
import { ProgressBar } from '../shared/ProgressBar'

import styles from './bonusFooter.module.scss'

interface BonusFooterProps {
  bonusStatus: StoreBonusStatusResponse
  onOpenDrawer: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const BonusFooter = memo(({ bonusStatus, onOpenDrawer }: BonusFooterProps) => {
  const dispatch = useAppDispatch()
  const t = useTranslations()
  const gameSlug = useGameSlug()
  const { items } = useCartContext()
  const { openModal } = useModalContext()
  const { bonusItems } = extractBonusValues({ bonusStatus, items })

  const tentativeBonusesDeliveredOnCheckout = bonusStatus ? getDeliveredBonuses({ bonusStatus, items }) : []
  const progressPercentage = bonusStatus ? getProgressPercentage({ bonusStatus, items }) : 0
  const remainingPoints = bonusStatus ? getRemainingPoints({ bonusStatus, items }) : 0
  const nextItem = getNextItem({ bonusStatus, items })
  const bonusTrackCompletionStatus = getBonusTrackCompletionStatus({ bonusStatus, items })

  const isCompletedBonusTrack =
    bonusTrackCompletionStatus === 'EXHAUSTIVE' || bonusTrackCompletionStatus === 'TENTATIVE'

  const isExhaustivelyCompletedBonusTrack = bonusTrackCompletionStatus === 'EXHAUSTIVE'
  const isUnavailableBonusTrack = bonusTrackCompletionStatus === 'UNAVAILABLE'

  useEffect(() => {
    if (isExhaustivelyCompletedBonusTrack) {
      dispatch(setBonusTrackCompleted(true))
    }
  }, [isExhaustivelyCompletedBonusTrack, dispatch])

  const unclaimedItems = useMemo(() => bonusItems.filter((item) => item.available && !item.claimed), [bonusItems])
  const unclaimedBonuses = unclaimedItems.length > 0 && tentativeBonusesDeliveredOnCheckout.length === 0
  const bonusesDeliveredOnCheckout = [...unclaimedItems, ...tentativeBonusesDeliveredOnCheckout]

  const unclaimedNotificationShown = hasUnclaimedBonusesCookie()
  useEffect(() => {
    if (unclaimedItems.length > 0 && !unclaimedNotificationShown) {
      openModal({ type: 'UNCLAIMED_BONUS', props: { unclaimedItems } })
    }
  }, [unclaimedItems, openModal, unclaimedNotificationShown])

  const displayItemDeliveryComponent = bonusesDeliveredOnCheckout.length > 0 && !isUnavailableBonusTrack

  const {
    props: { srcSet },
  } = getImageProps({ alt: '', width: 400, height: 400, src: BrawlFlare })
  const backgroundImage = getBackgroundImage(srcSet)

  const CompletedState = () => (
    <StoreTypography tag="div" textSize="md" style="plain" overrideFont="supercellHeadline" lineHeight="tight">
      {t('bonus_track_completed')}
    </StoreTypography>
  )

  const UnavailableState = () => (
    <div className={styles.textContent}>
      <StoreTypography tag="div" textSize="body" style="plain" overrideFont="supercellHeadline" lineHeight="tight">
        {t('bonus_track_unavailable')}
      </StoreTypography>
      <StoreTypography
        tag="div"
        textSize="sm"
        style="plain"
        overrideFont="supercellText"
        lineHeight="normal"
        className={styles.unavailableText}
      >
        {t('bonus_track_check_back')}
      </StoreTypography>
    </div>
  )

  const DefaultState = () => (
    <>
      <div className={styles.textContent}>
        <div className={styles.remainingPoints}>
          <Image src={BonusTrackPointsIcon} width="24" height="16" alt="store bonus points" />
          <StoreTypography tag="span" textSize="md" style="plain" overrideFont="supercellHeadline" lineHeight="normal">
            {remainingPoints}
          </StoreTypography>

          <StoreTypography tag="span" textSize="xs" style="plain" overrideFont="supercellHeadline" lineHeight="normal">
            {t('points')}
          </StoreTypography>
        </div>

        <StoreTypography
          tag="span"
          textSize="sm"
          style="plain"
          overrideFont="supercellText"
          lineHeight="tight"
          className={styles.remainingPointsText}
        >
          {t('to_next_store_bonus')}
        </StoreTypography>
      </div>

      <ProgressBar progressPercentage={progressPercentage} />
    </>
  )

  return (
    <m.aside
      className={cx(styles.bonusStatusFooter, styles[gameSlug], {
        [styles.unclaimed]: unclaimedBonuses,
        [styles.unavailable]: isUnavailableBonusTrack,
        [styles.completed]: isCompletedBonusTrack,
      })}
      onClick={onOpenDrawer}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-cy="bonus-footer"
    >
      {displayItemDeliveryComponent && (
        <ItemDeliveryIndicator
          unclaimedItems={unclaimedItems}
          tentativeItems={tentativeBonusesDeliveredOnCheckout}
          completedBonusTrack={isCompletedBonusTrack}
        />
      )}

      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          {isCompletedBonusTrack ? (
            <CompletedState />
          ) : isUnavailableBonusTrack ? (
            <UnavailableState />
          ) : (
            <DefaultState />
          )}
        </div>

        <div className={styles.bonusFooterImage}>
          <div className={styles.bonusImageContainer}>
            <div className={styles.flareContainer} style={{ backgroundImage: backgroundImage }} />
            {isCompletedBonusTrack ? (
              <BonusItemImage status="completed" />
            ) : (
              <BonusItemImage item={nextItem} status="next" />
            )}
          </div>
        </div>
      </div>

      <div className={styles.closedButtonContainer}>
        <DrawerButton />
      </div>
    </m.aside>
  )
})

BonusFooter.displayName = 'BonusFooter'
